import { useState, useEffect, useRef, useCallback } from 'react';
import { AudioRecorder, encodeAudioForAPI, AudioQueue } from '@/utils/audioUtils';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useApiTokens } from '@/hooks/useApiTokens';
import { useToast } from '@/hooks/use-toast';
import { VoiceAssistantToolsService } from '@/services/voice/VoiceAssistantToolsService';

interface Message {
  id: string;
  content: string;
  type: 'user' | 'assistant';
  timestamp: Date;
  isAudio?: boolean;
}

interface OpenAITool {
  type: "function";
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

interface UseRealtimeChatProps {
  systemPrompt?: string;
  onSpeakingChange?: (speaking: boolean) => void;
}

export const useRealtimeChat = (props?: UseRealtimeChatProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { apiTokens } = useApiTokens(currentBrandId);

  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceTools, setVoiceTools] = useState<OpenAITool[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Initialize audio context and queue
  useEffect(() => {
    const initAudioSystem = async () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }
      if (!audioQueueRef.current && audioContextRef.current) {
        audioQueueRef.current = new AudioQueue(audioContextRef.current);
      }
    };

    initAudioSystem();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Fetch voice tools when brand changes
  useEffect(() => {
    const fetchTools = async () => {
      if (!currentBrandId) {
        setVoiceTools([]);
        return;
      }
      try {
        const tools = await VoiceAssistantToolsService.getActiveToolsForBrand(currentBrandId);
        setVoiceTools(VoiceAssistantToolsService.convertToOpenAITools(tools));
        // Voice tools loaded silently
      } catch (err) {
        console.error('Failed to fetch voice tools:', err);
        setVoiceTools([]);
      }
    };
    fetchTools();
  }, [currentBrandId]);

  const playAudioData = useCallback(async (audioData: Uint8Array) => {
    if (audioQueueRef.current) {
      await audioQueueRef.current.addToQueue(audioData);
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    if (!currentBrandId || !apiTokens?.openai) {
      console.error('Missing brand ID or OpenAI API key');
      return null;
    }

    console.log('Setting up WebSocket connection...');
    
    // Use the full Supabase project URL for WebSocket
    const wsUrl = `wss://hjodtxoylcvcwjgvuctk.functions.supabase.co/realtime-chat`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      setIsConnecting(false);

      // Generate dynamic instructions from tools
      const toolDescriptions = voiceTools.map(t => `- ${t.name}: ${t.description}`).join('\n');
      const dynamicInstructions = voiceTools.length > 0 
        ? `You are a helpful AI business assistant. You have access to the following functions:\n\n${toolDescriptions}\n\nAlways be helpful and provide clear, actionable insights.`
        : (props?.systemPrompt || 'You are a helpful assistant.');

      // Send session configuration with dynamic tools
      const sessionConfig = {
        type: 'session.update',
        session: {
          modalities: ['text', 'audio'],
          instructions: dynamicInstructions,
          voice: 'alloy',
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          input_audio_transcription: {
            model: 'whisper-1'
          },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 1000
          },
          tools: voiceTools,
          tool_choice: voiceTools.length > 0 ? 'auto' : undefined,
          temperature: 0.8,
          max_response_output_tokens: 'inf'
        }
      };

      console.log('Sending session config with', voiceTools.length, 'tools');
      ws.send(JSON.stringify(sessionConfig));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'response.audio.delta':
            setIsSpeaking(true);
            props?.onSpeakingChange?.(true);
            
            // Use requestIdleCallback to decode audio without blocking
            requestIdleCallback(() => {
              try {
                const binaryString = atob(data.delta);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                playAudioData(bytes);
              } catch (err) {
                console.error('Audio decode error:', err);
              }
            }, { timeout: 100 });
            break;

          case 'response.audio.done':
            setIsSpeaking(false);
            props?.onSpeakingChange?.(false);
            break;

          case 'response.audio_transcript.delta':
            setTranscript(prev => prev + data.delta);
            break;

          case 'response.audio_transcript.done':
            // Add assistant message
            setMessages(prev => [...prev, {
              id: Date.now().toString(),
              content: data.transcript || transcript,
              type: 'assistant',
              timestamp: new Date(),
              isAudio: true
            }]);
            setTranscript('');
            break;

          case 'input_audio_buffer.speech_started':
            console.log('User started speaking');
            setTranscript('');
            break;

          case 'input_audio_buffer.speech_stopped':
            console.log('User stopped speaking');
            // The server will automatically create a response
            break;

          case 'conversation.item.input_audio_transcription.completed':
            console.log('User speech transcribed:', data.transcript);
            // Add user message
            setMessages(prev => [...prev, {
              id: Date.now().toString(),
              content: data.transcript,
              type: 'user',
              timestamp: new Date(),
              isAudio: true
            }]);
            break;

          case 'error':
            console.error('WebSocket error:', data.error);
            toast({
              title: "Connection Error",
              description: data.error.message || 'An error occurred',
              variant: "destructive",
            });
            break;

          default:
            console.log('Unhandled message type:', data.type);
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnecting(false);
      toast({
        title: "Connection Error",
        description: "Failed to connect to voice service",
        variant: "destructive",
      });
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
      setIsConnecting(false);
      setIsRecording(false);
      setIsSpeaking(false);
      setTranscript('');
    };

    return ws;
  }, [currentBrandId, apiTokens?.openai, props?.systemPrompt, props?.onSpeakingChange, playAudioData, toast, transcript, voiceTools]);

  const connect = useCallback(async () => {
    if (isConnecting || isConnected) return;

    if (!currentBrandId) {
      toast({
        title: "No Brand Selected",
        description: "Please select a brand first",
        variant: "destructive",
      });
      return;
    }

    if (!apiTokens?.openai) {
      toast({
        title: "API Key Missing",
        description: "OpenAI API key is required for voice chat",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const ws = setupWebSocket();
      if (ws) {
        wsRef.current = ws;
      }
    } catch (error) {
      console.error('Error connecting:', error);
      setIsConnecting(false);
      toast({
        title: "Connection Failed",
        description: "Could not access microphone or connect to service",
        variant: "destructive",
      });
    }
  }, [isConnecting, isConnected, currentBrandId, apiTokens?.openai, setupWebSocket, toast]);

  const disconnect = useCallback(() => {
    
    if (audioRecorderRef.current) {
      audioRecorderRef.current.stop();
      audioRecorderRef.current = null;
    }

    if (audioQueueRef.current) {
      audioQueueRef.current.clear();
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsRecording(false);
    setIsSpeaking(false);
    setTranscript('');
  }, []);

  const startRecording = useCallback(async () => {
    if (!isConnected || isRecording || !wsRef.current) return;

    try {
      console.log('Starting audio recording...');
      
      audioRecorderRef.current = new AudioRecorder((audioData) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const encodedAudio = encodeAudioForAPI(audioData);
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: encodedAudio
          }));
        }
      });

      await audioRecorderRef.current.start();
      setIsRecording(true);

      console.log('Audio recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Error",
        description: "Could not start audio recording",
        variant: "destructive",
      });
    }
  }, [isConnected, isRecording, toast]);

  const stopRecording = useCallback(() => {
    if (!isRecording || !audioRecorderRef.current) return;

    console.log('Stopping audio recording...');
    
    audioRecorderRef.current.stop();
    audioRecorderRef.current = null;
    setIsRecording(false);

    console.log('Audio recording stopped');
  }, [isRecording]);

  const sendTextMessage = useCallback((text: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: text
          }
        ]
      }
    };

    wsRef.current.send(JSON.stringify(event));
    wsRef.current.send(JSON.stringify({type: 'response.create'}));

    // Add user message to UI
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      content: text,
      type: 'user',
      timestamp: new Date(),
      isAudio: false
    }]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    messages,
    isConnected,
    isConnecting,
    isRecording,
    isSpeaking,
    transcript,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    sendTextMessage,
    clearMessages
  };
};